import {createRootRoute, Outlet, useRouter} from '@tanstack/react-router';
import {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {Toaster} from 'react-hot-toast';
import {tolgeeInstance, TolgeeProvider} from 'tolgee';

import {AuthService} from '~api/auth.service';
import {getUtilStateIsLoading} from '~redux/reducers/globalReducer';

import 'external-svg-loader';

import {PORTAL_ROOT_ID} from '~constants/constants';
import {useModelsQuery} from '~api/models.queries';
import {useCurrentProjectQuery} from '~api/projects.queries';
import {
  inspectionTypeQueryParam,
  loginRedirectQueryParam,
  modelIdQueryParam,
  projectIdQueryParam,
  replaceRoute,
  Route as Routes,
  useQueryParam,
} from '~utils/routeUtil';
import {useLaunchDarklyIdentify} from '~hooks/launchDarkly.hooks';
import {useContextSwitchGalleryReset} from '~hooks/useContextSwitchGalleryReset';
import {useOrgIdFromUrl} from '~hooks/useOrgIdFromUrl';
import {useReclaimOrgOnFocus} from '~hooks/useReclaimOrgOnFocus';
import {annotatorActions} from '~redux/reducers/annotatorReducer';
import {
  isProjectInUserData,
  selectCurrentProjectId,
  selectCustomer,
  selectProjects,
  selectUserEmail,
  selectUserId,
} from '~redux/reducers/userReducer';
import {fetchUserData, setCurrentProjectId} from '~redux/actions/userActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {BuildEnvInfo} from '~components/common/BuildEnvInfo';
import {GlobalLoadingIndicator} from '~components/common/GlobalLoadingIndicator';
import {ThirdPartyScipts} from '~components/common/ThirdPartyScipts';
import {AskAiProvider} from '~components/ask-ai/AskAiContext';
import {AskAiFloatingButton} from '~components/ask-ai/AskAiFloatingButton';
import {AskAiPanel} from '~components/ask-ai/AskAiPanel';
import {DefineRoiProvider} from '~components/images/annotator/hooks/useDefineRoi';
import {updateAbility} from 'src/contexts/AbilitiesContext';
import {LabelInstancesProvider} from 'src/contexts/LabelInstancesContext';

const intercomQueryParam = 'intercom';
const intercomModelRoutes = new Set<string>([
  Routes.modelSamples,
  Routes.modelSamplesImage,
  Routes.modelTraining,
  Routes.modelFinetune,
  Routes.modelFinetuneImage,
]);

const modelRoutesAllowingMissingId = new Set<string>([
  Routes.modelFinetune,
  Routes.modelFinetuneImage,
  Routes.modelTraining,
]);

const allowsMissingModelIdQueryParam = (pathname: string): boolean =>
  modelRoutesAllowingMissingId.has(pathname) || pathname.endsWith('/measuring');

const isTestEnv = import.meta.env.NODE_ENV === 'test';
const isE2eBuild = import.meta.env.VITE_E2E === 'true';

function RootLayout() {
  return (
    <LabelInstancesProvider>
      <DefineRoiProvider>
        <AskAiProvider>
          <AppWrapper>
            <Outlet />
          </AppWrapper>
        </AskAiProvider>
      </DefineRoiProvider>
    </LabelInstancesProvider>
  );
}

function AppWrapper({children}: {children: ReactNode}) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const location = router.state.location;
  const pathname = location?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const searchParams = useMemo(() => {
    if (location?.search) {
      return location.search as Record<string, string | undefined>;
    }
    if (typeof window !== 'undefined') {
      return Object.fromEntries(new URLSearchParams(window.location.search).entries());
    }
    return {};
  }, [location]);

  const showLoadingSpinner = useAppSelector(getUtilStateIsLoading);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const currentProject = useCurrentProjectQuery();
  const userEmail = useAppSelector(selectUserEmail);
  const userId = useAppSelector(selectUserId);
  const customer = useAppSelector(selectCustomer);
  const lastFetchedUserContext = useRef<string>();
  const projects = useAppSelector(selectProjects);
  const [projectId, setProjectIdQueryParam] = useQueryParam(projectIdQueryParam);
  const [, setModelIdQueryParam] = useQueryParam(modelIdQueryParam);
  const [, setInspectionTypeParam] = useQueryParam(inspectionTypeQueryParam);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const previousProjectIdRef = useRef<string | null>();
  const modelIdSeenWithPreviousProjectRef = useRef<string | undefined>();

  // Reset itemsPerPage and stale gallery image state when switching between contexts
  useContextSwitchGalleryReset();

  // Auto-switch customer when the app is opened via a link carrying an `orgID`
  // that differs from the current customer (and the user has access to it).
  const {needsCustomerSwitch, isSwitchingCustomer} = useOrgIdFromUrl();

  // When another tab switched the shared token to a different org, re-claim a
  // token for this tab's org as soon as the user returns to it.
  const {isReclaimingOrg} = useReclaimOrgOnFocus();

  // Keep the LaunchDarkly context in sync with the logged-in user and their organization
  useLaunchDarklyIdentify();

  const isIndexPage = pathname === Routes.index;
  const isNoProjectsPage = pathname === Routes.noProjects;

  const rawIntercomQuery = searchParams?.[intercomQueryParam];
  const intercomFlag = Array.isArray(rawIntercomQuery) ? rawIntercomQuery[0] : rawIntercomQuery;
  const isIntercomNavigation = typeof intercomFlag === 'string' && intercomFlag.toLowerCase() !== 'false';
  const routeRequiresModel = intercomModelRoutes.has(pathname);
  const hasProjectContext = Boolean(currentProjectId);
  const shouldLoadModels = router.state.location.pathname
    ? isIntercomNavigation && routeRequiresModel && hasProjectContext
    : false;
  const modelsQuery = useModelsQuery(shouldLoadModels);
  const waitingForIntercomModel = shouldLoadModels && (modelsQuery.isLoading || modelsQuery.isFetching);
  const awaitingIntercomContext =
    isIntercomNavigation && routeRequiresModel && (!hasProjectContext || waitingForIntercomModel);

  const latestModelId = useMemo(() => {
    if (!modelsQuery.data || modelsQuery.data.length === 0) {
      return undefined;
    }

    const nonArchivedModel = modelsQuery.data.find((model) => !model.archived);
    return (nonArchivedModel ?? modelsQuery.data[0])?.id;
  }, [modelsQuery.data]);

  const language = tolgeeInstance.getLanguage();

  useEffect(() => {
    if (isTestEnv) {
      return;
    }

    if (AuthService.isLoggedIn() && currentProject.data && userEmail) {
      const userRole = currentProject.data.users.find((user) => user.email === userEmail)?.role;
      if (userRole) {
        updateAbility(userRole);
      }
    }
  }, [currentProject.data, userEmail]);

  useEffect(() => {
    const fetchUser = async () => {
      const redirectUrl = (() => {
        if (typeof window !== 'undefined' && window.location.pathname && window.location.pathname.trim().length > 1) {
          return `${window.location.pathname}${window.location.search}${window.location.hash}`;
        }
        if (pathname && pathname.trim().length > 1) {
          return `${pathname}`;
        }
        return undefined;
      })();
      if (
        !AuthService.isLoggedIn() &&
        pathname !== Routes.index &&
        !pathname.includes('503') &&
        pathname !== Routes.authCallback
      ) {
        await replaceRoute(Routes.index, {query: {[loginRedirectQueryParam]: redirectUrl}});
      } else if (AuthService.isLoggedIn()) {
        const userContext = `${userId}:${customer ?? ''}`;
        if (isE2eBuild && userId !== '' && lastFetchedUserContext.current === userContext) {
          return;
        }
        lastFetchedUserContext.current = userContext;
        try {
          setIsFetchingUser(true);
          dispatch(fetchUserData());
        } catch (error) {
          await AuthService.logout({redirectUrl});
        } finally {
          setIsFetchingUser(false);
        }
      }
    };

    fetchUser();
    // We don't want to re-fetch user data if customer changes as it is part of the user data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pathname]);

  useEffect(() => {
    // Ensure that a projectId is set at all times (either from query param or from Redux store)
    if (projectId && typeof projectId === 'string') {
      dispatch(setCurrentProjectId(projectId));
    } else if (currentProjectId) {
      setProjectIdQueryParam(currentProjectId);
    }
  }, [dispatch, projectId, setProjectIdQueryParam, currentProjectId]);

  useEffect(() => {
    // If the user does not have a project assigned, should redirect to the no-projects page
    if (currentProjectId === null && !isIndexPage && !isNoProjectsPage) {
      replaceRoute(Routes.noProjects);
    }
  }, [currentProjectId, isIndexPage, isNoProjectsPage]);

  useEffect(() => {
    if (isTestEnv) {
      return;
    }

    if (!isIntercomNavigation) {
      return;
    }

    if (routeRequiresModel && shouldLoadModels && !latestModelId) {
      return;
    }

    const projectIdFromQuery = searchParams?.[projectIdQueryParam];
    const existingProjectId =
      typeof projectIdFromQuery === 'string' && projectIdFromQuery.length > 0 ? projectIdFromQuery : undefined;

    const modelIdFromQuery = searchParams?.[modelIdQueryParam];
    const existingModelId =
      typeof modelIdFromQuery === 'string' && modelIdFromQuery.length > 0 ? modelIdFromQuery : undefined;

    const nextQuery: Record<string, string> = {};
    if (!existingProjectId && currentProjectId) {
      nextQuery[projectIdQueryParam] = currentProjectId;
    }

    if (routeRequiresModel && !existingModelId && latestModelId) {
      nextQuery[modelIdQueryParam] = latestModelId;
    }

    if (Object.keys(nextQuery).length > 0) {
      void replaceRoute(pathname, {
        query: {...(router.state.location.search as Record<string, string>), ...nextQuery},
      });
    }
  }, [
    router,
    pathname,
    searchParams,
    currentProjectId,
    latestModelId,
    routeRequiresModel,
    shouldLoadModels,
    isIntercomNavigation,
  ]);

  useEffect(() => {
    if (isTestEnv) {
      return;
    }

    const modelIdParam = searchParams?.[modelIdQueryParam];
    const isModelRoute = pathname.includes('/model/');
    const isModelIdMissing =
      modelIdParam === undefined || (Array.isArray(modelIdParam) ? modelIdParam[0] : modelIdParam) === '';
    const shouldSkipModelRedirect = intercomModelRoutes.has(pathname) && isModelIdMissing && awaitingIntercomContext;

    if (isModelRoute && !shouldSkipModelRedirect && !allowsMissingModelIdQueryParam(pathname) && isModelIdMissing) {
      replaceRoute(Routes.models);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, awaitingIntercomContext]);

  useEffect(() => {
    const projectIdFromUrl = searchParams?.[projectIdQueryParam];
    const projectIdValue = typeof projectIdFromUrl === 'string' ? projectIdFromUrl : undefined;
    if (!projects?.length || !projectIdValue) {
      return;
    }

    // A customer switch from the `orgID` URL param is pending: don't bounce to the
    // invalid-project page, let the switch + reload land the user on the right org.
    if (needsCustomerSwitch) {
      return;
    }

    // Avoid redirect loops: when sitting on the invalid project page and the project becomes valid,
    // let that page handle the forward navigation instead of bouncing back here.
    if (pathname === Routes.invalidProject) {
      return;
    }

    if (!isProjectInUserData(projects, projectIdValue)) {
      replaceRoute(Routes.invalidProject, {
        query: {[projectIdQueryParam]: projectIdValue},
      });
    }
  }, [searchParams, projects, pathname, needsCustomerSwitch]);

  useEffect(() => {
    if (!isIntercomNavigation) {
      return;
    }

    if (routeRequiresModel && shouldLoadModels && !latestModelId) {
      return;
    }

    const projectIdFromQuery = searchParams?.[projectIdQueryParam];
    const existingProjectId =
      typeof projectIdFromQuery === 'string' && projectIdFromQuery.length > 0 ? projectIdFromQuery : undefined;

    const modelIdFromQuery = searchParams?.[modelIdQueryParam];
    const existingModelId =
      typeof modelIdFromQuery === 'string' && modelIdFromQuery.length > 0 ? modelIdFromQuery : undefined;

    const nextQuery: Record<string, string> = {};
    if (!existingProjectId && currentProjectId) {
      nextQuery[projectIdQueryParam] = currentProjectId;
    }

    if (routeRequiresModel && !existingModelId && latestModelId) {
      nextQuery[modelIdQueryParam] = latestModelId;
    }

    if (Object.keys(nextQuery).length > 0) {
      void replaceRoute(pathname, {
        query: {...(router.state.location.search as Record<string, string>), ...nextQuery},
      });
    }
  }, [
    router,
    pathname,
    searchParams,
    currentProjectId,
    latestModelId,
    routeRequiresModel,
    shouldLoadModels,
    isIntercomNavigation,
  ]);

  useEffect(() => {
    const modelIdParam = searchParams?.[modelIdQueryParam];
    const isModelRoute = pathname.includes('/model/');
    const isModelIdMissing =
      modelIdParam === undefined || (Array.isArray(modelIdParam) ? modelIdParam[0] : modelIdParam) === '';
    const shouldSkipModelRedirect = intercomModelRoutes.has(pathname) && isModelIdMissing && awaitingIntercomContext;

    if (isModelRoute && !shouldSkipModelRedirect && !allowsMissingModelIdQueryParam(pathname) && isModelIdMissing) {
      replaceRoute(Routes.models);
    }
  }, [pathname, searchParams, awaitingIntercomContext]);

  useEffect(() => {
    if (language && document.documentElement.lang !== language) {
      document.documentElement.lang = language;
    }
  }, [language]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const modelIdParam = urlParams.get(modelIdQueryParam) ?? undefined;
    const urlProjectId = urlParams.get(projectIdQueryParam) ?? undefined;
    if (!currentProjectId) {
      previousProjectIdRef.current = currentProjectId ?? null;
      modelIdSeenWithPreviousProjectRef.current = modelIdParam;
      return;
    }

    const projectChanged = Boolean(previousProjectIdRef.current) && previousProjectIdRef.current !== currentProjectId;
    if (projectChanged) {
      if (modelIdParam === modelIdSeenWithPreviousProjectRef.current) {
        setModelIdQueryParam(undefined);
        setInspectionTypeParam(undefined);
        dispatch(annotatorActions.setCurrentModel(undefined));
      }
      previousProjectIdRef.current = currentProjectId;
      modelIdSeenWithPreviousProjectRef.current = modelIdParam;
      return;
    }

    previousProjectIdRef.current = currentProjectId;
    if (!urlProjectId || urlProjectId === currentProjectId) {
      modelIdSeenWithPreviousProjectRef.current = modelIdParam;
    }
  }, [currentProjectId, dispatch, setInspectionTypeParam, setModelIdQueryParam]);

  // Don't block the auth callback route - it needs to render to process the token
  const isAuthCallbackRoute = pathname === Routes.authCallback;
  const shouldShowBlockingSpinner =
    !isTestEnv &&
    !pathname.includes('503') &&
    !isAuthCallbackRoute &&
    (isFetchingUser ||
      isSwitchingCustomer ||
      needsCustomerSwitch ||
      isReclaimingOrg ||
      (!isIndexPage && currentProjectId?.length === 0));

  return (
    <>
      <TolgeeProvider
        tolgee={tolgeeInstance}
        fallback={<GlobalLoadingIndicator show={true} />} // loading fallback
        ssr
        options={{
          useSuspense: false, // disable suspense to avoid hydration issues
        }}
      >
        {!isTestEnv && !isE2eBuild && <ThirdPartyScipts />}

        {shouldShowBlockingSpinner ? (
          <GlobalLoadingIndicator show />
        ) : (
          <>
            {children}
            {!isTestEnv && <GlobalLoadingIndicator show={showLoadingSpinner} />}
          </>
        )}

        {!isTestEnv && <AskAiPanel />}
        {!isTestEnv && <AskAiFloatingButton />}
        <Toaster position='bottom-right' />
        <BuildEnvInfo />
        <div id={PORTAL_ROOT_ID} />
      </TolgeeProvider>
    </>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
});
